import { Stars } from '@react-three/drei';
import { useMemo } from 'react';
import { BackSide, Color } from 'three';

/**
 * Galactic backdrop: a smooth deep navy gradient sphere wrapped
 * around the camera plus two layered drei `<Stars>` fields — a far
 * dense one for the "thousands of distant pinpricks" feel and a
 * sparser closer one to give the field parallax depth.
 */
const BG_VERTEX = /* glsl */ `
  varying vec3 vDir;
  void main() {
    vDir = normalize(position);
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const BG_FRAGMENT = /* glsl */ `
  precision highp float;
  uniform vec3 uColorTop;
  uniform vec3 uColorBottom;
  uniform vec3 uColorSide;
  varying vec3 vDir;
  void main() {
    float t = clamp(vDir.y * 0.55 + 0.5, 0.0, 1.0);
    vec3 col = mix(uColorBottom, uColorTop, smoothstep(0.0, 1.0, t));
    // Subtle horizontal mix toward the sides — keeps the gradient
    // from looking like a flat gradient strip.
    float side = pow(abs(vDir.x), 1.4) * 0.18;
    col = mix(col, uColorSide, side);
    gl_FragColor = vec4(col, 1.0);
  }
`;

export function Background() {
  const uniforms = useMemo(
    () => ({
      uColorTop: { value: new Color('#0c1d6b') },
      uColorBottom: { value: new Color('#040a30') },
      uColorSide: { value: new Color('#0a1450') },
    }),
    [],
  );

  return (
    <>
      <mesh frustumCulled={false} renderOrder={-10}>
        <sphereGeometry args={[160, 48, 24]} />
        <shaderMaterial
          vertexShader={BG_VERTEX}
          fragmentShader={BG_FRAGMENT}
          uniforms={uniforms}
          side={BackSide}
          depthWrite={false}
        />
      </mesh>

      {/* Thousands of tiny far-away pinpricks */}
      <Stars
        radius={140}
        depth={70}
        count={5500}
        factor={1.4}
        saturation={0}
        fade
        speed={0.18}
      />

      {/* Closer, slightly brighter and warmer layer for parallax */}
      <Stars
        radius={70}
        depth={35}
        count={1400}
        factor={2.6}
        saturation={0.25}
        fade
        speed={0.4}
      />
    </>
  );
}
